import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../config/baseUrl";
import { REPORT_COLOR_ENCODING } from "../../config/colorEncoding";
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { ToastMessgae } from "../utils/toast";

export default function Analytics() {
  const [graphType, setGraphType] = useState("pie_chart");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRange, setDateRange] = useState("today");
  const [company, setCompany] = useState("");
  const [depo, setDepo] = useState("");
  const [busNumber, setBusNumber] = useState("");
  const [depoData, setDepoData] = useState([]);
  const [companyNameData, setCompanyNameData] = useState([]);
  const [pieChartApiData, setPieChartApiData] = useState(null);
  const [barChartApiData, setBarChartApiData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loginUser = JSON.parse(localStorage.getItem("user")) || {};

  // Fetch pie chart data from API
  const fetchPieChartData = useCallback(async () => {
    // Only fetch for pie charts
    if (graphType !== "pie_chart") {
      return;
    }

    // Validate start_date and end_date - they must be in pair (both selected or both empty)
    // If one is selected, the other must also be selected
    if ((startDate && !endDate) || (!startDate && endDate)) {
      ToastMessgae({
        code: 400,
        message: "Please select both start date and end date together, or leave both empty.",
      });
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();

      // Add start_date and end_date if both are selected
      if (startDate && endDate) {
        params.append("start_date", startDate);
        params.append("end_date", endDate);
      }

      // Add company only for SUPER_ADMIN
      if (loginUser?.user_type === "SUPER_ADMIN" && company) {
        params.append("company", company);
      }

      // Add depo
      if (depo) {
        params.append("depo", depo);
      }

      // Add buses (bus number)
      if (busNumber) {
        params.append("buses", busNumber);
      }

      const url = params.toString()
        ? `${baseUrl}/module/mobile/pie_chart/?${params.toString()}`
        : `${baseUrl}/module/mobile/pie_chart/`;


      const response = await axios.get(url, {
        headers: {
          Authorization: "Token " + localStorage.getItem("token"),
        },
      });


      if (response?.data?.data) {
        setPieChartApiData(response.data.data);
      } else {
        console.warn("No data in API response:", response.data);
      }
    } catch (error) {
      console.error("Error fetching pie chart data:", error);
      alert("Failed to fetch pie chart data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [
    graphType,
    startDate,
    endDate,
    company,
    depo,
    busNumber,
    loginUser?.user_type,
  ]);

  // Convert date range dropdown to start_date and end_date
  const convertDateRangeToDates = useCallback(() => {
    if (!dateRange) return { startDate: null, endDate: null };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let startDate = null;
    let endDate = null;

    switch (dateRange) {
      case "today":
        startDate = new Date(today);
        endDate = new Date(today);
        break;

      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = yesterday;
        endDate = yesterday;
        break;

      case "last_week":
        const weekStart = new Date(today);
        weekStart.setDate(weekStart.getDate() - 6);
        startDate = weekStart;
        endDate = today;
        break;

      case "last_month":
        const monthStart = new Date(today);
        monthStart.setDate(monthStart.getDate() - 29);
        startDate = monthStart;
        endDate = today;
        break;

      default:
        return { startDate: null, endDate: null };
    }

    // Format dates as YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
    };
  }, [dateRange]);

  // Fetch bar chart data from API
  const fetchBarChartData = useCallback(async () => {
    // Only fetch for bar graphs
    if (graphType !== "bar_graph") {
      return;
    }

    // Convert date range to start_date and end_date
    const { startDate: convertedStartDate, endDate: convertedEndDate } =
      convertDateRangeToDates();

    setLoading(true);
    try {
      const params = new URLSearchParams();

      // Add start_date and end_date from date range
      if (convertedStartDate && convertedEndDate) {
        params.append("start_date", convertedStartDate);
        params.append("end_date", convertedEndDate);
      }

      // Add company only for SUPER_ADMIN
      if (loginUser?.user_type === "SUPER_ADMIN" && company) {
        params.append("company", company);
      }

      // Add depo
      if (depo) {
        params.append("depo", depo);
      }

      // Add buses (bus number)
      if (busNumber) {
        params.append("buses", busNumber);
      }

      const url = params.toString()
        ? `${baseUrl}/module/mobile/bar_chart/?${params.toString()}`
        : `${baseUrl}/module/mobile/bar_chart/`;


      const response = await axios.get(url, {
        headers: {
          Authorization: "Token " + localStorage.getItem("token"),
        },
      });


      if (response?.data?.data && Array.isArray(response.data.data)) {
        setBarChartApiData(response.data.data);
      } else {
        console.warn("No data in API response:", response.data);
        setBarChartApiData([]);
      }
    } catch (error) {
      console.error("Error fetching bar chart data:", error);
      alert("Failed to fetch bar chart data. Please try again.");
      setBarChartApiData([]);
    } finally {
      setLoading(false);
    }
  }, [
    graphType,
    company,
    depo,
    busNumber,
    loginUser?.user_type,
    convertDateRangeToDates,
  ]);

  // Transform API data to chart format
  const getCleaningTypeData = () => {
    // Only return data if API data is available
    if (!pieChartApiData) {
      return [];
    }
    
    let data = [
      {
        name: "Regular Cleaning",
        value: pieChartApiData.regular_bus_count || 0,
        color: REPORT_COLOR_ENCODING.REGULAR_CLEANING,
      },
      {
        name: "Standard Deep Cleaning",
        value: pieChartApiData.standard_bus_count || 0,
        color: REPORT_COLOR_ENCODING.STANDARD_DEEP_CLEANING,
      },
      {
        name: "Annual Deep Cleaning",
        value: pieChartApiData.annual_bus_count || 0,
        color: REPORT_COLOR_ENCODING.ANNUAL_DEEP_CLEANING,
      },
    ];
    
    // Filter out "Regular Cleaning" for Cleaning Manager role
    // if (loginUser?.user_type === "CLEANING_MANAGER") {
      return data.filter(item => item.name !== "Regular Cleaning");
    // }
    
    return data;
  };

  const getIssueTypeData = () => {
    // Only return data if API data is available
    if (!pieChartApiData) {
      return [];
    }
    
    return [
      {
        name: "Lost Property",
        value: pieChartApiData.issue_buses_count || 0,
        color: REPORT_COLOR_ENCODING.ISSUE_IMAGE,
      },
      {
        name: "Graffiti",
        value: pieChartApiData.graffiti_buses_count || 0,
        color: REPORT_COLOR_ENCODING.GRAFFITI,
      },
      {
        name: "Damaged Issue",
        value: pieChartApiData.damage_buses_count || 0,
        color: REPORT_COLOR_ENCODING.DAMAGE,
      },
      {
        name: "Other",
        value: pieChartApiData.other_buses_count || 0,
        color: REPORT_COLOR_ENCODING.OTHER_ISSUE,
      },
    ];
  };

  const getEmergencyData = () => {
    // Only return data if API data is available
    if (!pieChartApiData) {
      return [];
    }
    
    return [
      {
        name: "Emergency button pushed",
        value: pieChartApiData.emergency_bus_count || 0,
        color: REPORT_COLOR_ENCODING.EMERGENCY,
      },
      {
        name: "Non-Emergency",
        value: pieChartApiData.without_emergency_bus_count || 0,
        color: REPORT_COLOR_ENCODING.NON_EMERGENCY,
      },
    ];
  };

  // Static data for Cleaning Type Chart (fallback)
  const cleaningTypeData = [
    {
      name: "Regular Cleaning",
      value: 45,
      color: REPORT_COLOR_ENCODING.REGULAR_CLEANING,
    },
    {
      name: "Standard Deep Cleaning",
      value: 30,
      color: REPORT_COLOR_ENCODING.STANDARD_DEEP_CLEANING,
    },
    {
      name: "Annual Deep Cleaning",
      value: 25,
      color: REPORT_COLOR_ENCODING.ANNUAL_DEEP_CLEANING,
    },
  ];

  // Static data for Issue Type Chart
  const issueTypeData = [
    {
      name: "Lost Property",
      value: 35,
      color: REPORT_COLOR_ENCODING.ISSUE_IMAGE,
    },
    {
      name: "Graffiti",
      value: 40,
      color: REPORT_COLOR_ENCODING.GRAFFITI,
    },
    {
      name: "Damaged Issue",
      value: 25,
      color: REPORT_COLOR_ENCODING.DAMAGE,
    },
    {
      name: "Other",
      value: 0,
      color: REPORT_COLOR_ENCODING.OTHER_ISSUE,
    },
  ];

  // Static data for Emergency Chart
  const emergencyData = [
    {
      name: "Emergency button pushed",
      value: 20,
      color: REPORT_COLOR_ENCODING.EMERGENCY,
    },
    {
      name: "Non-Emergency",
      value: 80,
      color: REPORT_COLOR_ENCODING.NON_EMERGENCY,
    },
  ];

  // Fetch depo list
  const fetchDepoList = useCallback(
    async (companyId = null) => {
      try {
        let url = `${baseUrl}/module/admin/depo-list`;

        // For SUPER_ADMIN, if company is selected, filter depo list by company
        if (loginUser?.user_type === "SUPER_ADMIN" && companyId) {
          url += `?company=${companyId}`;
        }

        const response = await axios(url, {
          method: "GET",
          headers: {
            Authorization: "Token " + localStorage.getItem("token"),
          },
        });
        setDepoData(response?.data?.data || []);
      } catch (error) {
        console.error("depo fetch error:", error);
        setDepoData([]);
      }
    },
    [loginUser?.user_type]
  );

  // Fetch company list
  const fetchCompanyList = async () => {
    try {
      const response = await axios(`${baseUrl}/module/admin/company-list`, {
        method: "GET",
        headers: {
          Authorization: "Token " + localStorage.getItem("token"),
        },
      });
      setCompanyNameData(response?.data?.data || []);
    } catch (error) {
      console.error("company list error:", error);
      setCompanyNameData([]);
    }
  };

  useEffect(() => {
    // For SUPER_ADMIN: only fetch depo list if company is selected
    // For other users: fetch depo list normally
    if (loginUser?.user_type === "SUPER_ADMIN") {
      fetchCompanyList();
      // Only fetch depo list if company is selected
      if (company) {
        fetchDepoList(company);
      } else {
        setDepoData([]); // Clear depo list when no company selected
      }
    } else {
      fetchDepoList();
    }
    
    // Automatically fetch pie chart data on initial load
    if (graphType === "pie_chart") {
      fetchPieChartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // For SUPER_ADMIN: Fetch depo list when company changes
  useEffect(() => {
    if (loginUser?.user_type === "SUPER_ADMIN") {
      if (company) {
        fetchDepoList(company);
        // Clear depo selection when company changes
        setDepo("");
      } else {
        setDepoData([]);
        setDepo("");
      }
    }
  }, [company, fetchDepoList, loginUser?.user_type]);

  // Automatically fetch chart data when graph type changes
  useEffect(() => {
    if (graphType === "pie_chart") {
      fetchPieChartData();
    } else if (graphType === "bar_graph") {
      fetchBarChartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphType]);

  // Chart data will only be fetched when Apply Filter button is clicked
  // Removed automatic trigger on graphType change

  // Generate bar chart data from API response
  const getBarChartData = (baseData, chartType) => {
    // If API data is available, use it
    if (
      barChartApiData &&
      Array.isArray(barChartApiData) &&
      barChartApiData.length > 0
    ) {
      return barChartApiData.map((item) => {
        const entry = {
          name: item.end_date || item.date || "",
        };


        // Map API data based on chart type
        if (chartType === "cleaning") {
          // Exclude "Regular" for Cleaning Manager role
          // if (loginUser?.user_type !== "CLEANING_MANAGER") {
          //   entry["Regular"] = item.regular_bus_count || 0;
          // }
          entry["Standard"] = item.standard_bus_count || 0;
          entry["Annual"] = item.annual_bus_count || 0;
        } else if (chartType === "issue") {
          entry["Lost"] = item.issue_buses_count || 0;
          entry["Graffiti"] = item.graffiti_buses_count || 0;
          entry["Damaged"] = item.damage_buses_count || 0;
          entry["Other"] = item.other_buses_count || 0;
        } else if (chartType === "emergency") {
          entry["Emergency"] = item.emergency_bus_count || 0;
          entry["Non-Emergency"] = item.without_emergency_bus_count || 0;
        }
        return entry;
      });
    }

    // Fallback: return empty array to show "no data available"
    return [];
  };

  // Calculate totals from bar chart API data for legends
  const getBarChartTotals = (chartType) => {
    if (
      barChartApiData &&
      Array.isArray(barChartApiData) &&
      barChartApiData.length > 0
    ) {
      if (chartType === "cleaning") {
        const totals = barChartApiData.reduce(
          (acc, item) => {
            acc.regular += item.regular_bus_count || 0;
            acc.standard += item.standard_bus_count || 0;
            acc.annual += item.annual_bus_count || 0;
            return acc;
          },
          { regular: 0, standard: 0, annual: 0 }
        );
        const data = [
          // {
          //   name: "Regular Cleaning",
          //   value: totals.regular,
          //   color: REPORT_COLOR_ENCODING.REGULAR_CLEANING,
          // },
          {
            name: "Standard Deep Cleaning",
            value: totals.standard,
            color: REPORT_COLOR_ENCODING.STANDARD_DEEP_CLEANING,
          },
          {
            name: "Annual Deep Cleaning",
            value: totals.annual,
            color: REPORT_COLOR_ENCODING.ANNUAL_DEEP_CLEANING,
          },
        ];
        // Filter out "Regular Cleaning" for Cleaning Manager role
        if (loginUser?.user_type === "CLEANING_MANAGER") {
          return data.filter(item => item.name !== "Regular Cleaning");
        }
        return data;
      } else if (chartType === "issue") {
        const totals = barChartApiData.reduce(
          (acc, item) => {
            acc.lost += item.issue_buses_count || 0;
            acc.graffiti += item.graffiti_buses_count || 0;
            acc.damaged += item.damage_buses_count || 0;
            acc.other += item.other_buses_count || 0;
            return acc;
          },
          { lost: 0, graffiti: 0, damaged: 0, other: 0 }
        );
        return [
          {
            name: "Lost Property",
            value: totals.lost,
            color: REPORT_COLOR_ENCODING.ISSUE_IMAGE,
          },
          {
            name: "Grafiti",
            value: totals.graffiti,
            color: REPORT_COLOR_ENCODING.GRAFFITI,
          },
          {
            name: "Damaged Issue",
            value: totals.damaged,
            color: REPORT_COLOR_ENCODING.DAMAGE,
          },
          {
            name: "Other",
            value: totals.other,
            color: REPORT_COLOR_ENCODING.OTHER_ISSUE,
          },
        ];
      } else if (chartType === "emergency") {
        const totals = barChartApiData.reduce(
          (acc, item) => {
            acc.emergency += item.emergency_bus_count || 0;
            acc.nonEmergency += item.without_emergency_bus_count || 0;
            return acc;
          },
          { emergency: 0, nonEmergency: 0 }
        );
        return [
          {
            name: "Emergency button pushed",
            value: totals.emergency,
            color: REPORT_COLOR_ENCODING.EMERGENCY,
          },
          {
            name: "Non-Emergency",
            value: totals.nonEmergency,
            color: REPORT_COLOR_ENCODING.NON_EMERGENCY,
          },
        ];
      }
    }
    // Fallback to static data
    return null;
  };

  const handleFilter = (e) => {
    e.preventDefault();

    // For pie charts, fetch API data
    if (graphType === "pie_chart") {
      fetchPieChartData();
    } else if (graphType === "bar_graph") {
      // For bar graphs, fetch API data
      fetchBarChartData();
    }
  };

  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    setDateRange("today");
    setCompany("");
    setDepo("");
    setBusNumber("");
    setPieChartApiData(null);
    setBarChartApiData(null);
  };

  // Download pie chart data
  const handleDownloadPieChart = async () => {
    // Validate start_date and end_date - they must be in pair (both selected or both empty)
    if ((startDate && !endDate) || (!startDate && endDate)) {
      alert(
        "Please select both start date and end date together, or leave both empty."
      );
      return;
    }

    try {
      const params = new URLSearchParams();

      // Add start_date and end_date if both are selected
      if (startDate && endDate) {
        params.append("start_date", startDate);
        params.append("end_date", endDate);
      }

      // Add company only for SUPER_ADMIN
      if (loginUser?.user_type === "SUPER_ADMIN" && company) {
        params.append("company", company);
      }

      // Add depo
      if (depo) {
        params.append("depo", depo);
      }

      // Add buses (bus number)
      if (busNumber) {
        params.append("buses", busNumber);
      }

      const url = params.toString()
        ? `${baseUrl}/module/mobile/download_pie_chart/?${params.toString()}`
        : `${baseUrl}/module/mobile/download_pie_chart/`;

      
      const response = await axios.get(url, {
        headers: {
          Authorization: "Token " + localStorage.getItem("token"),
        },
        responseType: "blob", // Important for file downloads
      });

      // Create a blob from the response
      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create a temporary URL and trigger download
      const urlObject = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = urlObject;
      link.setAttribute("download", `pie_chart_analytics.xlsx`);
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.remove();
      window.URL.revokeObjectURL(urlObject);
    } catch (error) {
      console.error("Error downloading pie chart data:", error);
      alert("Failed to download pie chart data. Please try again.");
    }
  };

  // Download bar chart data
  const handleDownloadBarChart = async () => {
    // Convert date range to start_date and end_date
    const { startDate: convertedStartDate, endDate: convertedEndDate } =
      convertDateRangeToDates();

    try {
      const params = new URLSearchParams();

      // Add start_date and end_date from date range
      if (convertedStartDate && convertedEndDate) {
        params.append("start_date", convertedStartDate);
        params.append("end_date", convertedEndDate);
      }

      // Add company only for SUPER_ADMIN
      if (loginUser?.user_type === "SUPER_ADMIN" && company) {
        params.append("company", company);
      }

      // Add depo
      if (depo) {
        params.append("depo", depo);
      }

      // Add buses (bus number)
      if (busNumber) {
        params.append("buses", busNumber);
      }

      const url = params.toString()
        ? `${baseUrl}/module/mobile/download_bar_chart/?${params.toString()}`
        : `${baseUrl}/module/mobile/download_bar_chart/`;


      const response = await axios.get(url, {
        headers: {
          Authorization: "Token " + localStorage.getItem("token"),
        },
        responseType: "blob", // Important for file downloads
      });

      // Create a blob from the response
      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create a temporary URL and trigger download
      const urlObject = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = urlObject;
      link.setAttribute("download", `bar_chart_analytics.xlsx`);
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.remove();
      window.URL.revokeObjectURL(urlObject);
    } catch (error) {
      console.error("Error downloading bar chart data:", error);
      alert("Failed to download bar chart data. Please try again.");
    }
  };

  

  return (
    <>
      <style>
        {`
          .recharts-pie-sector:focus,
          .recharts-pie-sector:active,
          .recharts-pie-sector:hover {
            outline: none !important;
            border: none !important;
            stroke: none !important;
          }
          .recharts-active-shape {
            outline: none !important;
            border: none !important;
            stroke: none !important;
          }
          .recharts-layer:focus {
            outline: none !important;
          }
          .recharts-pie-sector path {
            outline: none !important;
            stroke: none !important;
          }
          .recharts-pie-sector path:focus,
          .recharts-pie-sector path:active,
          .recharts-pie-sector path:hover {
            outline: none !important;
            stroke: none !important;
            border: none !important;
          }
          .recharts-sector:focus,
          .recharts-sector:active {
            outline: none !important;
            stroke: none !important;
          }
          svg .recharts-pie-sector path {
            outline: none !important;
            stroke: none !important;
          }
          .recharts-wrapper *:focus {
            outline: none !important;
          }
        `}
      </style>
      <section className="main-sec">
        <div className="row">
          <div className="col-lg-12">
            <div className="dashboard-title">
              <h4 className="dash-head">
                <i className="fa fa-chart-pie me-2" />
                Analytics
              </h4>
            </div>
            <div className="custom-bredcump">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Analytics
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>

        {/* Date Filter Section */}
        <div className="row mt-3 mb-4">
          <div className="col-lg-12">
            <div className="cards">
              <div className="row align-items-end">
                <div className="col-lg-3 col-md-6 mb-3">
                  <label htmlFor="graphType" className="form-label">
                    Graph Type
                  </label>
                  <select
                    className="form-select"
                    id="graphType"
                    value={graphType}
                    onChange={(e) => setGraphType(e.target.value)}
                  >
                    <option value="pie_chart">Pie Chart</option>
                    <option value="bar_graph">Bar Graph</option>
                  </select>
                </div>
                {loginUser?.user_type === "SUPER_ADMIN" && (
                  <div className="col-lg-3 col-md-6 mb-3">
                    <label htmlFor="company" className="form-label">
                      Company
                    </label>
                    <select
                      className="form-select"
                      id="company"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                    >
                      <option value="">Select Company</option>
                      {companyNameData?.map((arr) => (
                        <option value={arr?.id} key={arr?.id}>
                          {arr?.company_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="col-lg-3 col-md-6 mb-3">
                  <label htmlFor="depot" className="form-label">
                    Depot
                  </label>
                  <select
                    className="form-select"
                    id="depot"
                    value={depo}
                    onChange={(e) => setDepo(e.target.value)}
                    disabled={
                      loginUser?.user_type === "SUPER_ADMIN" && !company
                    }
                  >
                    <option value="">
                      {loginUser?.user_type === "SUPER_ADMIN" && !company
                        ? "Select Company First"
                        : "Select Depot"}
                    </option>
                    {depoData?.map((arr) => (
                      <option value={arr?.id} key={arr?.id}>
                        {arr?.depo_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-3 col-md-6 mb-3">
                  <label htmlFor="busNumber" className="form-label">
                    Bus Number
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="busNumber"
                    placeholder="Bus Number"
                    value={busNumber}
                    onChange={(e) => setBusNumber(e.target.value)}
                  />
                </div>
                {graphType === "pie_chart" ? (
                  <>
                    <div className="col-lg-3 col-md-6 mb-3">
                      <label htmlFor="startDate" className="form-label">
                        Start Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        id="startDate"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          // If start date is selected but end date is not, show warning
                          if (e.target.value && !endDate) {
                            // Validation will happen on filter apply
                          }
                        }}
                      />
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                      <label htmlFor="endDate" className="form-label">
                        End Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        id="endDate"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          // If end date is selected but start date is not, show warning
                          if (e.target.value && !startDate) {
                            // Validation will happen on filter apply
                          }
                        }}
                        min={startDate || undefined}
                      />
                    </div>
                  </>
                ) : (
                  <div className="col-lg-3 col-md-6 mb-3">
                    <label htmlFor="dateRange" className="form-label">
                      Date Range
                    </label>
                    <select
                      className="form-select"
                      id="dateRange"
                      value={dateRange}
                      onChange={(e) => setDateRange(e.target.value)}
                    >
                      <option value="">Select Date Range</option>
                      <option value="today">Today</option>
                      <option value="yesterday">Yesterday</option>
                      <option value="last_week">Last 1 Week</option>
                      <option value="last_month">Last 1 Month</option>
                    </select>
                  </div>
                )}
                <div className="col-lg-3 col-md-6 mb-3">
                  <button
                    className="thm-btn w-100"
                    onClick={(e) => {
                      handleFilter(e);
                    }}
                    type="button"
                  >
                    Apply Filter
                  </button>
                </div>
                <div className="col-lg-3 col-md-6 mb-3">
                  <button
                    className="thm-btn text-white bg-dark border-dark w-100"
                    onClick={
                      graphType === "pie_chart"
                        ? handleDownloadPieChart
                        : handleDownloadBarChart
                    }
                    type="button"
                  >
                    <i className="fa fa-download me-2" />
                    Download {graphType === "pie_chart" ? "Pie Chart" : "Bar Chart"}
                  </button>
                </div>
              </div>
              {(startDate ||
                endDate ||
                dateRange ||
                company ||
                depo ||
                busNumber) && (
                <div className="row mt-3">
                  <div className="col-lg-12">
                    <button
                      className="thm-btn"
                      onClick={handleClearFilter}
                      type="button"
                    >
                      Clear Filter
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Charts Section */}
        {graphType === "pie_chart" ? (
          <div className="row mt-4">
            {loading && (
              <div className="col-12 text-center mb-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading chart data...</p>
              </div>
            )}
            {/* Chart 1: Cleaning Type */}
            <div className="col-lg-4 col-md-6 mb-4">
              <div className="cards">
                <h4 className="dash-head2 mb-3">Cleaning Type Distribution</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={getCleaningTypeData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      activeShape={false}
                      stroke="none"
                    >
                      {getCleaningTypeData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                {getCleaningTypeData().length > 0 && (
                  <div
                    className="chart-legend mt-3"
                    style={{ minHeight: "120px" }}
                  >
                    <div className="row">
                      {(() => {
                        const data = getCleaningTypeData();
                        const total = data.reduce(
                          (sum, item) => sum + item.value,
                          0
                        );
                        return data.map((item, index) => {
                          const percentage =
                            total > 0
                              ? ((item.value / total) * 100).toFixed(2)
                              : 0;
                          return (
                            <div key={index} className="col-12 mb-2">
                              <div className="d-flex align-items-center">
                                <span
                                  className="legend-color"
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    backgroundColor: item.color,
                                    borderRadius: "50%",
                                    display: "inline-block",
                                    marginRight: "8px",
                                  }}
                                />
                                <span>
                                  {item.name}: {item.value} buses ({percentage}%)
                                </span>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Chart 2: Issue Type */}
            <div className="col-lg-4 col-md-6 mb-4">
              <div className="cards">
                <h4 className="dash-head2 mb-3">Issue Type Distribution</h4>
                {getIssueTypeData().length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={getIssueTypeData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        activeShape={false}
                        stroke="none"
                      >
                        {getIssueTypeData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        iconType="circle"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div
                    style={{
                      height: "300px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#666",
                      fontSize: "16px",
                    }}
                  >
                    {loading ? "Loading chart data..." : "No data available"}
                  </div>
                )}
                {getIssueTypeData().length > 0 && (
                  <div
                    className="chart-legend mt-3"
                    style={{ minHeight: "120px" }}
                  >
                    <div className="row">
                      {(() => {
                        const data = getIssueTypeData();
                        const total = data.reduce(
                          (sum, item) => sum + item.value,
                          0
                        );
                        return data.map((item, index) => {
                          const percentage =
                            total > 0
                              ? ((item.value / total) * 100).toFixed(2)
                              : 0;
                          return (
                            <div key={index} className="col-12 mb-2">
                              <div className="d-flex align-items-center">
                                <span
                                  className="legend-color"
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    backgroundColor: item.color,
                                    borderRadius: "50%",
                                    display: "inline-block",
                                    marginRight: "8px",
                                  }}
                                />
                                <span>
                                  {item.name}: {item.value} buses ({percentage}%)
                                </span>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Chart 3: Emergency vs Non-Emergency */}
            <div className="col-lg-4 col-md-6 mb-4">
              <div className="cards">
                <h4 className="dash-head2 mb-3">Emergency button pushed</h4>
                {getEmergencyData().length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={getEmergencyData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        activeShape={false}
                        stroke="none"
                      >
                        {getEmergencyData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        iconType="circle"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div
                    style={{
                      height: "300px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#666",
                      fontSize: "16px",
                    }}
                  >
                    {loading ? "Loading chart data..." : "No data available"}
                  </div>
                )}
                {getEmergencyData().length > 0 && (
                  <div
                    className="chart-legend mt-3"
                    style={{ minHeight: "120px" }}
                  >
                    <div className="row">
                      {(() => {
                        const data = getEmergencyData();
                        const total = data.reduce(
                          (sum, item) => sum + item.value,
                          0
                        );
                        return data.map((item, index) => {
                          const percentage =
                            total > 0
                              ? ((item.value / total) * 100).toFixed(2)
                              : 0;
                          return (
                            <div key={index} className="col-12 mb-2">
                              <div className="d-flex align-items-center">
                                <span
                                  className="legend-color"
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    backgroundColor: item.color,
                                    borderRadius: "50%",
                                    display: "inline-block",
                                    marginRight: "8px",
                                  }}
                                />
                                <span>
                                  {item.name}: {item.value} buses ({percentage}%)
                                </span>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="row mt-4">
            {/* Bar Chart 1: Cleaning Type */}
            <div className="col-lg-4 col-md-6 mb-4">
              <div className="cards">
                <h4 className="dash-head2 mb-3">Cleaning Type Distribution</h4>
                {getBarChartData(cleaningTypeData, "cleaning").length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={getBarChartData(cleaningTypeData, "cleaning")}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis />
                      <Tooltip />
                      {(loginUser?.user_type === "CLEANING_MANAGER" 
                        ? cleaningTypeData.filter(item => item.name !== "Regular Cleaning")
                        : cleaningTypeData
                      ).map((item, index) => {
                        const firstWord = item.name.split(" ")[0];
                        const filteredData = loginUser?.user_type === "CLEANING_MANAGER" 
                          ? cleaningTypeData.filter(item => item.name !== "Regular Cleaning")
                          : cleaningTypeData;
                        return (
                          <Bar
                            key={index}
                            dataKey={firstWord}
                            name={item.name}
                            fill={item.color}
                            radius={
                              index === filteredData.length - 1
                                ? [8, 8, 0, 0]
                                : [0, 0, 0, 0]
                            }
                          />
                        );
                      })}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div
                    style={{
                      height: "400px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#666",
                      fontSize: "16px",
                    }}
                  >
                    No data available
                  </div>
                )}
                <div
                  className="chart-legend mt-3"
                  style={{ minHeight: "120px" }}
                >
                  <div className="row">
                    {(() => {
                      const totals = getBarChartTotals("cleaning");
                      const data = totals || cleaningTypeData;
                      const filteredData = loginUser?.user_type === "CLEANING_MANAGER" 
                        ? data.filter(item => item.name !== "Regular Cleaning")
                        : data;
                      return filteredData.map((item, index) => (
                        <div key={index} className="col-12 mb-2">
                          <div className="d-flex align-items-center">
                            <span
                              className="legend-color"
                              style={{
                                width: "16px",
                                height: "16px",
                                backgroundColor: item.color,
                                borderRadius: "4px",
                                display: "inline-block",
                                marginRight: "8px",
                              }}
                            />
                            <span>
                              {item.name}: {item.value} buses
                            </span>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Bar Chart 2: Issue Type */}
            <div className="col-lg-4 col-md-6 mb-4">
              <div className="cards">
                <h4 className="dash-head2 mb-3">Issue Type Distribution</h4>
                {getBarChartData(issueTypeData, "issue").length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={getBarChartData(issueTypeData, "issue")}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis />
                      <Tooltip />
                      {issueTypeData.map((item, index) => {
                        const firstWord = item.name.split(" ")[0];
                        return (
                          <Bar
                            key={index}
                            dataKey={firstWord}
                            name={item.name}
                            fill={item.color}
                            radius={
                              index === issueTypeData.length - 1
                                ? [8, 8, 0, 0]
                                : [0, 0, 0, 0]
                            }
                          />
                        );
                      })}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div
                    style={{
                      height: "400px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#666",
                      fontSize: "16px",
                    }}
                  >
                    No data available
                  </div>
                )}
                <div
                  className="chart-legend mt-3"
                  style={{ minHeight: "120px" }}
                >
                  <div className="row">
                    {(getBarChartTotals("issue") || issueTypeData).map(
                      (item, index) => (
                        <div key={index} className="col-12 mb-2">
                          <div className="d-flex align-items-center">
                            <span
                              className="legend-color"
                              style={{
                                width: "16px",
                                height: "16px",
                                backgroundColor: item.color,
                                borderRadius: "4px",
                                display: "inline-block",
                                marginRight: "8px",
                              }}
                            />
                            <span>
                              {item.name.split(" ")[0]}: {item.value} buses
                            </span>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Bar Chart 3: Emergency button pushed */}
            <div className="col-lg-4 col-md-6 mb-4">
              <div className="cards">
                <h4 className="dash-head2 mb-3">Emergency button pushed</h4>
                {getBarChartData(emergencyData, "emergency").length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={getBarChartData(emergencyData, "emergency")}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis />
                      <Tooltip />
                      {/* {emergencyData.map((item, index) => { */}

                      {(getBarChartTotals("emergency") || emergencyData).map((item, index) => {

                        const firstWord = item.name.split(" ")[0];
                        return (
                          <Bar
                            key={index}
                            dataKey={firstWord}
                            name={item.name}
                            fill={item.color}
                            radius={
                              index === emergencyData.length - 1
                                ? [8, 8, 0, 0]
                                : [0, 0, 0, 0]
                            }
                          />
                        );
                      })}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div
                    style={{
                      height: "400px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#666",
                      fontSize: "16px",
                    }}
                  >
                    No data available
                  </div>
                )}
                <div
                  className="chart-legend mt-3"
                  style={{ minHeight: "120px" }}
                >
                  <div className="row">
                    {(getBarChartTotals("emergency") || emergencyData).map(
                      (item, index) => (
                        <div key={index} className="col-12 mb-2">
                          <div className="d-flex align-items-center">
                            <span
                              className="legend-color"
                              style={{
                                width: "16px",
                                height: "16px",
                                backgroundColor: item.color,
                                borderRadius: "4px",
                                display: "inline-block",
                                marginRight: "8px",
                              }}
                            />
                            <span>
                              {item.name}: {item.value} buses
                              {/* {item.name.split(" ")[0]}: {item.value} buses */}
                            </span>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
